import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import SchedulingPage from './SchedulingPage';
import { 
  Package, 
  Shield, 
  Eye, 
  BarChart3, 
  Users, 
  CheckCircle, 
  TrendingUp,
  Clock,
  FileText,
  AlertTriangle,
  ChevronRight,
  Smartphone,
  ArrowRight
} from 'lucide-react';

const WarehouseMonitoring: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<'landing' | 'scheduling'>('landing');

  const handleScheduleDemo = () => {
    setCurrentPage('scheduling');
  };

  const handleBackToLanding = () => {
    setCurrentPage('landing');
  };

  if (currentPage === 'scheduling') {
    return <SchedulingPage onBack={handleBackToLanding} />;
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Warehouse Phone Monitoring | Forklift Safety & Compliance"
        description="Prevent warehouse accidents with shift-only phone monitoring. OSHA-ready logs, supervisor alerts, no content access. Built for distribution & logistics."
        canonical="https://nixspaces.com/warehouse-phone-monitoring"
        keywords="warehouse phone monitoring, forklift safety monitoring, warehouse compliance, shift monitoring, OSHA warehouse logs"
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
              <span className="text-2xl font-bold text-gray-900">Spaces</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a>
              <a href="#benefits" className="text-gray-600 hover:text-blue-600 transition-colors">Benefits</a>
              <Link to="/pricing" className="text-gray-600 hover:text-blue-600 transition-colors">Pricing</Link>
              <a href="#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
            <button 
              onClick={handleScheduleDemo}
              className="bg-cyan-500 text-white px-4 py-2 rounded-2xl hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request Demo
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-cyan-100 px-4 py-2 rounded-full mb-6">
                <Package className="h-4 w-4 text-cyan-600" />
                <span className="text-sm font-semibold text-cyan-700">Built for Warehouse Operations</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Prevent Forklift Accidents From Phone Distraction
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Monitor employee phone activity during warehouse shifts only. Generate OSHA-ready compliance logs, alert supervisors to excessive usage, and maintain worker focus near equipment—without reading message content.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={handleScheduleDemo}
                  className="bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <span>Schedule Demo</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <Link 
                  to="/pricing"
                  className="border-2 border-cyan-500 text-cyan-500 px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-500 hover:text-white transition-all duration-300 text-center shadow-lg hover:shadow-xl"
                >
                  View Pricing
                </Link>
              </div>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Reduce Forklift Accidents</h3>
                    <p className="text-gray-600">Alert operators using phones near equipment</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Monitor Shift Activity</h3>
                    <p className="text-gray-600">Track usage during work hours, not breaks</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Compliance Documentation</h3>
                    <p className="text-gray-600">Automated logs for safety audits and OSHA</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Manager Dashboard</h3>
                    <p className="text-gray-600">Real-time visibility for supervisors and EHS teams</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">Distraction Risk</div>
              <p className="text-gray-600">Phone distraction is a leading cause of preventable warehouse accidents</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">Time Loss</div>
              <p className="text-gray-600">Personal phone use during shifts reduces productivity and focus</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">Liability Exposure</div>
              <p className="text-gray-600">Lack of monitoring documentation increases compliance risk</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features for Warehouse */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Purpose-Built for Warehouse Safety
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Monitor phone activity without invading privacy during breaks or off-hours
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Clock className="h-7 w-7 text-cyan-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Work Hours Only</h3>
              <p className="text-gray-600">
                Monitoring activates only during scheduled shifts. Breaks, lunch, and off-hours are private.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <AlertTriangle className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Real-Time Alerts</h3>
              <p className="text-gray-600">
                Instant notifications to supervisors when excessive usage is detected during critical operations.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-violet-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <FileText className="h-7 w-7 text-violet-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Compliance Logs</h3>
              <p className="text-gray-600">
                Detailed timestamped records for safety audits, incident investigations, and regulatory compliance.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-amber-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <BarChart3 className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Usage Analytics</h3>
              <p className="text-gray-600">
                Team and individual usage reports help identify patterns and improve training programs.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-indigo-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="h-7 w-7 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Manager Dashboard</h3>
              <p className="text-gray-600">
                Live view for warehouse supervisors and EHS managers with drill-down by shift, zone, or team.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100">
              <div className="bg-rose-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-rose-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Privacy Controls</h3>
              <p className="text-gray-600">
                Clear employee notice, no content monitoring—only activity timestamps and usage duration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="benefits" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Warehouse Use Cases
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Proven solutions for the safety and compliance challenges facing warehouse operations
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-3xl border border-cyan-100">
              <Package className="h-12 w-12 text-cyan-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Forklift & Equipment Safety</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>Prevent distracted driving near forklifts and heavy machinery</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>Document compliance for post-incident investigations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>Reduce accident liability and workers' comp claims</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-3xl border border-emerald-100">
              <TrendingUp className="h-12 w-12 text-emerald-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Productivity & Efficiency</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Reduce time wasted on personal device usage during shifts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Improve pick rates and order accuracy metrics</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Create accountability culture without micromanagement</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-8 rounded-3xl border border-violet-100">
              <Shield className="h-12 w-12 text-violet-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Compliance & Risk Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span>Meet OSHA distracted-worker documentation requirements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span>Maintain audit trails for safety certifications</span>
                </li>
                <li className="flex items-start space-x-2">
                  <ChevronRight className="h-5 w-5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span>Protect against negligence and liability claims</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Warehouse Phone Monitoring FAQs
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about monitoring policies, privacy, and deployment
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Does monitoring run during breaks or off-hours?</h3>
              <p className="text-gray-700">
                No. Nix Spaces is configured to monitor only during scheduled work shifts. Lunch breaks, personal breaks, and all time outside of work hours are completely private—no monitoring occurs when employees are off the clock or on designated break time.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Can Nix Spaces read text messages or see photos?</h3>
              <p className="text-gray-700">
                No. Nix Spaces tracks <em>when</em> and <em>how long</em> the phone is actively used, but it does not access message content, photos, videos, emails, or any personal data. Monitoring is limited to activity timestamps and duration only.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">How do employees know monitoring is active?</h3>
              <p className="text-gray-700">
                Employers must provide clear written notice to all monitored employees before deployment. The app provides on-device indicators when monitoring is active. Transparency and employee awareness are built into the system.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">What triggers a manager alert?</h3>
              <p className="text-gray-700">
                Managers configure usage thresholds based on their policies (e.g., extended usage during critical operations). When an employee exceeds those thresholds, the system sends a real-time alert to designated supervisors so they can address the behavior promptly.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Who is Nix Spaces designed for?</h3>
              <p className="text-gray-700">
                Nix Spaces is purpose-built for warehouse managers, operations supervisors, and safety teams overseeing distribution centers, logistics facilities, and any environment where phone distraction near forklifts or equipment creates safety risk.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">How long does deployment take?</h3>
              <p className="text-gray-700">
                Deployment typically takes 1–2 weeks including policy setup, employee notice, app installation, and manager training. We provide setup guidance and onboarding support to ensure smooth rollout.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Are compliance logs audit-ready?</h3>
              <p className="text-gray-700">
                Yes. All activity logs are timestamped, tamper-evident, and exportable for OSHA inspections, incident investigations, and safety audits. Reports clearly document when monitoring was active and what usage occurred during work hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 md:p-12 rounded-3xl border border-gray-200">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                From the Team Behind GoPoint NIX
              </h2>
              <p className="text-lg text-gray-700">
                Nix Spaces is developed by the proven team behind <strong>GoPoint NIX</strong>, an established distracted-driving prevention solution trusted in fleet management and transportation safety.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Safety Expertise</h3>
                <p className="text-gray-700">Years of proven experience building phone monitoring technology for safety-critical environments.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">Privacy by Design</h3>
                <p className="text-gray-700">Work-hours-only monitoring with transparent employee notice, no content monitoring, and clear data boundaries.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ready to Improve Warehouse Safety?</h2>
            <p className="text-xl text-gray-600">
              Schedule a personalized demo to see how Nix Spaces reduces accidents and improves productivity in your operation.
            </p>
          </div>
          
          <div className="text-center">
            <button 
              onClick={handleScheduleDemo}
              className="bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl mx-auto"
            >
              <span>Schedule Your Demo</span>
              <ChevronRight className="h-5 w-5" />
            </button>
            <p className="text-gray-500 mt-4">No credit card required • 15-minute consultation</p>
          </div>
        </div>
      </section>

      {/* Policy Kit CTA */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-cyan-100 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-cyan-500 mb-6">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need a Phone Policy First?
            </h2>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Before implementing monitoring, establish clear rules with our free Shift Distraction Policy Kit for warehouses. Includes sample policy, rollout checklist, and employee forms.
            </p>
            <Link
              to="/shift-distraction-policy-kit"
              className="inline-flex items-center justify-center space-x-2 bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <FileText className="h-5 w-5" />
              <span>Download Free Policy Kit</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-8 w-auto filter invert" />
              <span className="text-xl font-bold">Spaces</span>
            </Link>
            
            <div className="flex flex-wrap items-center justify-center space-x-6 text-sm">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/industrial-safety-monitoring" className="text-gray-400 hover:text-white transition-colors">
                Industrial Safety
              </Link>
              <Link to="/pricing" className="text-gray-400 hover:text-white transition-colors">
                Pricing
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                Terms of Service
              </Link>
              <span className="text-gray-400">
                © 2024 Nix Spaces. All rights reserved.
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WarehouseMonitoring;
